import { useEffect, useRef, useState } from "react";
import axios from "axios";
import host from "../global";

// Candidates see the company through the company profile: invite emails name
// the company, and the apply page shows its name and About Company section.
// Self-serve signups start with neither. So before an invite goes out or an
// apply link is made, confirmCompanyDetails() checks what candidates would
// miss and, when something is missing, asks first. The dialog itself is
// CompanyProfileCheckHost, mounted once per portal.

let openDialog = null;
// Once filled in these stay filled in, so only a "yes" is remembered.
const known = { name: false, about: false };
// "Skip for now" on the question before a scheduling pop-up covers only that
// scheduling: the check runs again when the invite is sent and must not ask a
// second time, but the next time a scheduling pop-up opens it asks again.
const skipped = new Set();

// Saved from the dialog itself: later checks this session go straight ahead
export const markCompanyNameSet = () => {
  known.name = true;
};

export const registerCompanyProfileDialog = (open) => {
  openDialog = open;
  return () => {
    if (openDialog === open) openDialog = null;
  };
};

/**
 * Resolves true to go ahead, false to stop.
 * purpose: "invite" (scheduling: needs the name) or "link" (apply link: needs
 * the name and the About Company section).
 * Never blocks on its own failure: if the check can't run, the action goes ahead.
 */
export async function confirmCompanyDetails(purpose = "invite", { fromGate = false } = {}) {
  const needsAbout = purpose === "link";
  if (!openDialog || (known.name && (!needsAbout || known.about))) return true;
  if (skipped.has(purpose)) return true;
  let status;
  try {
    const token = sessionStorage.getItem("accessToken");
    const res = await axios.get(`${host}/api/company-profile-status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    status = res.data;
  } catch (err) {
    console.error("Company profile check failed:", err);
    return true;
  }
  known.name = Boolean(status?.hasCompanyName);
  known.about = Boolean(status?.hasAboutCompany);
  const missingName = !known.name;
  const missingAbout = needsAbout && !known.about;
  if (!missingName && !missingAbout) return true;
  const goAhead = await openDialog({ purpose, missingName, missingAbout, isAdmin: Boolean(status?.isAdmin) });
  if (goAhead && fromGate && (!known.name || missingAbout)) skipped.add(purpose);
  return goAhead;
}

/**
 * Asks before a scheduling pop-up opens rather than when it is submitted.
 * Pass the pop-up's own open flag; render the pop-up with the value returned.
 * Stopping at the question (Cancel, or off to Company Profile) calls onCancel,
 * which should reset that flag.
 */
export function useCompanyDetailsGate(open, onCancel, purpose = "invite") {
  const [cleared, setCleared] = useState(false);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    if (!open) {
      setCleared(false);
      return undefined;
    }
    // A new scheduling: an earlier "Skip for now" doesn't carry over
    skipped.delete(purpose);
    let live = true;
    confirmCompanyDetails(purpose, { fromGate: true }).then((goAhead) => {
      if (!live) return;
      if (goAhead) setCleared(true);
      else cancelRef.current?.();
    });
    return () => {
      live = false;
    };
  }, [open, purpose]);
  return open && cleared;
}
